import { inject, Injectable, Signal, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

export interface AlertButton {
  label: string;
  role: string;
  styleClass?: string;
}

export interface AlertOptions {
  buttons: AlertButton[];
  description: string;
  title: string;
}

export interface ConfirmAlertOptions {
  cancelLabel?: string;
  confirmLabel?: string;
  description: string;
  title: string;
}

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private currentAlert = signal<AlertOptions | null>(null);
  private previousFocus: HTMLElement | null = null;
  private resolveResult: ((role: string) => void) | null = null;
  private router = inject(Router);

  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => this.close());
  }

  close(role = 'cancel'): void {
    this.dismiss(role, true);
  }

  get(): Signal<AlertOptions | null> {
    return this.currentAlert.asReadonly();
  }

  open(options: AlertOptions): Promise<string> {
    if (!this.currentAlert()) {
      this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    } else {
      this.dismiss('cancel', false);
    }
    this.currentAlert.set(options);
    return new Promise<string>((resolve) => {
      this.resolveResult = resolve;
    });
  }

  openConfirm(options: ConfirmAlertOptions): Promise<string> {
    return this.open({
      buttons: [
        { label: options.cancelLabel ?? 'Cancelar', role: 'cancel' },
        { label: options.confirmLabel ?? 'Confirmar', role: 'confirm' },
      ],
      description: options.description,
      title: options.title,
    });
  }

  private dismiss(role: string, restoreFocus: boolean): void {
    if (!this.currentAlert()) {
      return;
    }
    const previousFocus = this.previousFocus;
    this.currentAlert.set(null);
    if (restoreFocus) {
      this.previousFocus = null;
    }
    this.resolveResult?.(role);
    this.resolveResult = null;
    if (restoreFocus) {
      requestAnimationFrame(() => {
        if (!this.currentAlert() && previousFocus?.isConnected) {
          previousFocus.focus();
        }
      });
    }
  }
}
